import type {
  PreferenceAssignmentUpdate,
  PreferenceGroupDetail,
  PreferenceGroupPolicyUpdate,
  UpdatePreferenceGroupsRequest,
} from '../api/contracts'
import type { PreferenceGroupPreferenceItem, PreferenceGroupTab } from '../models'
import {
  preferenceChoiceChanged,
  preferenceChoiceValue,
  preferenceHasDefaultCopy,
} from '../logic/preferenceValues'

export function formatFullName(
  firstName: string | null,
  lastName: string | null,
  fallback: string | null
) {
  const parts = [firstName, lastName].filter((part): part is string => Boolean(part?.trim()))
  return parts.length > 0 ? parts.join(' ') : fallback?.trim() || 'Unnamed user'
}

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * A preference tab's part of an address: the last part of its key
 * (`preferences.export` → `export`), as in `/me/preferences/export`.
 */
export function tabSlug(tabKey: string) {
  return slugify(tabKey.trim().split('.').pop() ?? '')
}

/**
 * Whether an address's tab is this one: its slug, or an older address's full
 * key (`preferences.export`) or dashed key (`preferences-export`), which the
 * pages then rewrite to the slug.
 */
export function isTabAddress(tabKey: string, address: string) {
  return address === tabSlug(tabKey) || address === tabKey || address === slugify(tabKey)
}

export function preferenceIsEditable(preference: PreferenceGroupPreferenceItem) {
  return preference.overrides_allowed
}

export function preferenceDraftWithUserOverridesAllowed(
  preference: PreferenceGroupPreferenceItem,
  userOverridesAllowed: boolean
): PreferenceGroupPreferenceItem {
  return {
    ...preference,
    user_overrides_allowed: userOverridesAllowed,
  }
}

type PreferenceGroupLayoutNode = PreferenceGroupTab['layout_nodes'][number]

function getLayoutNodePreferences(
  node: PreferenceGroupLayoutNode
): PreferenceGroupPreferenceItem[] {
  return [...node.preferences, ...node.children.flatMap(getLayoutNodePreferences)]
}

function getPreferenceGroupPreferences(
  tabs: PreferenceGroupTab[]
): PreferenceGroupPreferenceItem[] {
  return tabs.flatMap((tab) => tab.layout_nodes.flatMap(getLayoutNodePreferences))
}

function mapLayoutNodePreference(
  node: PreferenceGroupLayoutNode,
  preferenceId: string,
  mapper: (preference: PreferenceGroupPreferenceItem) => PreferenceGroupPreferenceItem
): PreferenceGroupLayoutNode {
  return {
    ...node,
    preferences: node.preferences.map((preference) =>
      preference.preference_id === preferenceId ? mapper(preference) : preference
    ),
    children: node.children.map((child) => mapLayoutNodePreference(child, preferenceId, mapper)),
  }
}

export function mapPreferenceGroupPreference(
  group: PreferenceGroupDetail,
  preferenceId: string,
  mapper: (preference: PreferenceGroupPreferenceItem) => PreferenceGroupPreferenceItem
): PreferenceGroupDetail {
  return {
    ...group,
    tabs: group.tabs.map((tab) => ({
      ...tab,
      layout_nodes: tab.layout_nodes.map((node) =>
        mapLayoutNodePreference(node, preferenceId, mapper)
      ),
    })),
  }
}

function policyChanged(
  original: PreferenceGroupPreferenceItem,
  draft: PreferenceGroupPreferenceItem
) {
  return original.user_overrides_allowed !== draft.user_overrides_allowed
}

function assignmentUpdateForPreference(
  preferenceGroupId: string,
  preference: PreferenceGroupPreferenceItem
): PreferenceAssignmentUpdate {
  return {
    preference_id: preference.preference_id,
    assignee: { type: 'group', id: preferenceGroupId },
    value: preferenceChoiceValue(preference),
  }
}

function policyUpdateForPreference(
  preferenceGroupId: string,
  preference: PreferenceGroupPreferenceItem
): PreferenceGroupPolicyUpdate {
  return {
    preference_group_id: preferenceGroupId,
    preference_id: preference.preference_id,
    user_overrides_allowed: preference.user_overrides_allowed,
  }
}

export function buildPreferenceGroupUpdatePayload(
  originalGroup: PreferenceGroupDetail | undefined,
  draftGroup: PreferenceGroupDetail | undefined
): UpdatePreferenceGroupsRequest {
  const payload: UpdatePreferenceGroupsRequest = { assignments: [], policies: [] }
  if (!originalGroup || !draftGroup) return payload

  // A rename from the page's title.
  if (draftGroup.preference_group_label !== originalGroup.preference_group_label) {
    payload.groups = [
      {
        preference_group_id: draftGroup.preference_group_id,
        preference_group_label: draftGroup.preference_group_label,
      },
    ]
  }

  const originalByPreferenceId = new Map(
    getPreferenceGroupPreferences(originalGroup.tabs).map((preference) => [
      preference.preference_id,
      preference,
    ])
  )

  for (const draftPreference of getPreferenceGroupPreferences(draftGroup.tabs)) {
    const originalPreference = originalByPreferenceId.get(draftPreference.preference_id)
    if (!originalPreference) continue

    if (
      preferenceIsEditable(draftPreference) &&
      preferenceChoiceChanged(originalPreference, draftPreference)
    ) {
      payload.assignments?.push(
        assignmentUpdateForPreference(draftGroup.preference_group_id, draftPreference)
      )
    }

    if (
      preferenceIsEditable(draftPreference) &&
      policyChanged(originalPreference, draftPreference)
    ) {
      payload.policies?.push(
        policyUpdateForPreference(draftGroup.preference_group_id, draftPreference)
      )
    }
  }

  return payload
}

/**
 * The payload, plus dropping any group value that only copies the organization
 * default, so members follow the default again. Not a change on its own:
 * nothing anyone sees differs, so it rides along with a real one.
 */
export function withGroupDefaultCopiesDropped(
  payload: UpdatePreferenceGroupsRequest,
  draftGroup: PreferenceGroupDetail | undefined
): UpdatePreferenceGroupsRequest {
  if (!draftGroup || isEmptyPreferenceGroupPayload(payload)) return payload
  const updated = new Set((payload.assignments ?? []).map((update) => update.preference_id))
  const drops = getPreferenceGroupPreferences(draftGroup.tabs)
    .filter(
      (preference) =>
        preferenceIsEditable(preference) &&
        preferenceHasDefaultCopy(preference) &&
        !updated.has(preference.preference_id)
    )
    .map((preference) => ({
      preference_id: preference.preference_id,
      assignee: { type: 'group' as const, id: draftGroup.preference_group_id },
      value: null,
    }))
  return { ...payload, assignments: [...(payload.assignments ?? []), ...drops] }
}

export function isEmptyPreferenceGroupPayload(payload: UpdatePreferenceGroupsRequest) {
  return (
    (payload.groups?.length ?? 0) === 0 &&
    (payload.memberships?.length ?? 0) === 0 &&
    (payload.assignments?.length ?? 0) === 0 &&
    (payload.policies?.length ?? 0) === 0
  )
}
